import { useRef, useState } from "react";
import { Download, Play, ShieldCheck, Smartphone, UserPlus } from "lucide-react";
import { Button } from "./ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const SRC = `${API}/api/public/demo/captioned.mp4`;
const POSTER = "/demo/poster.jpg";

/* The 40-second product demo, shown before anyone decides whether to register.
   It is real screen recording of the product, so it is the strongest thing on this page — the
   trial terms sit directly under it so nobody reaches the form without seeing them. */
export const DemoVideo = ({ onStart, onPlay, showCta = true }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  const start = () => {
    setPlaying(true);
    onPlay?.();
    // a rejected play() (codec, autoplay policy, flaky network) must never surface as an
    // uncaught error on the signup page
    requestAnimationFrame(() => ref.current?.play().catch(() => {}));
  };

  return (
    <section data-testid="demo-video-card" className="mb-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
        See it in action
      </p>
      <div className="relative overflow-hidden rounded-xl border border-border bg-royal-deep
                      aspect-square">
        <video
          ref={ref}
          data-testid="demo-video"
          src={SRC}
          poster={POSTER}
          controls={playing}
          playsInline
          preload="metadata"
          onEnded={() => setPlaying(false)}
          className="h-full w-full object-cover"
        />
        {!playing && (
          <button
            type="button"
            data-testid="demo-video-play"
            onClick={start}
            aria-label="Play the 40-second product demo"
            className="absolute inset-0 flex flex-col items-center justify-center gap-3
                       bg-royal-deep/45 hover:bg-royal-deep/30 transition-colors duration-200"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold
                             shadow-lg transition-transform duration-200 hover:scale-105">
              <Play className="h-7 w-7 text-royal-deep translate-x-0.5" fill="currentColor" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-white">
              Watch the 40-second demo
            </span>
          </button>
        )}
      </div>
      <p className="mt-2.5 text-xs text-muted-foreground" data-testid="demo-video-blurb">
        Recorded from the live platform: the operations map, weather intelligence, automations,
        work orders, inspections and team messaging.
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
        <a data-testid="demo-download-facebook" href={`${API}/api/public/demo/facebook/download`}
           download
           className="inline-flex min-h-[44px] items-center gap-1.5 text-[11px] font-semibold
                      uppercase tracking-[0.14em] text-royal transition-colors hover:text-gold">
          <Download className="h-3.5 w-3.5" /> Download for Facebook
        </a>
        <a data-testid="demo-download-vertical" href={`${API}/api/public/demo/vertical/download`}
           download
           className="inline-flex min-h-[44px] items-center gap-1.5 text-[11px] uppercase
                      tracking-[0.14em] text-muted-foreground transition-colors hover:text-royal">
          <Smartphone className="h-3.5 w-3.5" /> Vertical for Reels
        </a>
      </div>
      {/* On the rebuilt splash the trial button and its terms live in the panel below, so this
          card does not repeat them — two identical gold buttons read as a bug. */}
      {showCta && (
        <>
          <Button
            data-testid="demo-video-start-btn"
            onClick={onStart}
            className="mt-4 w-full h-12 bg-gold hover:bg-gold/90 text-royal-deep font-semibold
                       text-base touch-manipulation transition-colors duration-200"
          >
            <UserPlus className="h-4 w-4 mr-2" /> Start Your 14-Day Free Trial
          </Button>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground"
             data-testid="demo-trial-terms">
            <ShieldCheck className="h-3.5 w-3.5 text-royal mt-0.5 shrink-0" />
            <span>
              14 days free. A credit card is required to start, and you are not charged until the
              trial ends — cancel any time before then.
            </span>
          </p>
        </>
      )}
    </section>
  );
};
